import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { SettingsSection } from '#src/components/settings/SettingsSection/index.tsx';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';

import { ApiKeyRow } from './ApiKeyRow/index.tsx';
import type {
  ApiKeysManagementQuery,
  ApiKeysManagementQueryVariables,
  CreateApiKeyMutation,
  CreateApiKeyMutationVariables,
} from './__generated__/index.graphql.ts';

const messages = defineMessages({
  title: {
    defaultMessage: 'API keys',
  },
  description: {
    defaultMessage:
      'Query the Sorare GraphQL API at a higher rate (up to 600 requests/minute). Pass your key in the <bold>APIKEY</bold> HTTP header.',
  },
  noKeys: {
    defaultMessage: 'You don’t have any API key yet.',
  },
  create: {
    defaultMessage: 'Create API key',
  },
});

const API_KEYS_MANAGEMENT_QUERY = gql`
  query ApiKeysManagementQuery {
    currentUser {
      id
      slug
      apiKeys {
        id
        ...ApiKeyRow_apiKey
      }
    }
  }
  ${ApiKeyRow.fragments.apiKey}
` as TypedDocumentNode<ApiKeysManagementQuery, ApiKeysManagementQueryVariables>;

const CREATE_API_KEY_MUTATION = gql`
  mutation CreateApiKeyMutation($input: createApiKeyInput!) {
    createApiKey(input: $input) {
      apiKey {
        id
        ...ApiKeyRow_apiKey
      }
      currentUser {
        id
        slug
        apiKeys {
          id
          ...ApiKeyRow_apiKey
        }
      }
      errors {
        message
      }
    }
  }
  ${ApiKeyRow.fragments.apiKey}
` as TypedDocumentNode<CreateApiKeyMutation, CreateApiKeyMutationVariables>;

export const ApiKeysManagement = () => {
  const { data, loading } = useQuery(API_KEYS_MANAGEMENT_QUERY);
  const [createApiKey, { loading: creating }] = useMutation(
    CREATE_API_KEY_MUTATION
  );

  const apiKeys = data?.currentUser?.apiKeys;

  return (
    <SettingsSection title={messages.title} description={messages.description}>
      {loading ? (
        <LoadingIndicator />
      ) : (
        <Vertical gap={2}>
          {apiKeys?.length ? (
            <Vertical gap={0}>
              {apiKeys.map(apiKey => (
                <ApiKeyRow key={apiKey.id} apiKey={apiKey} />
              ))}
            </Vertical>
          ) : (
            <BodyM color="var(--c-nd-600)">
              <FormattedMessage {...messages.noKeys} />
            </BodyM>
          )}
          <div>
            <Button
              size="medium"
              color="tertiary"
              loading={creating}
              onClick={() => {
                createApiKey({ variables: { input: {} } });
              }}
            >
              <FormattedMessage {...messages.create} />
            </Button>
          </div>
        </Vertical>
      )}
    </SettingsSection>
  );
};
